import React, { useState } from 'react';
import { Table, Button, Popover, List, Typography, Collapse, Card, Descriptions, Row, Col } from 'antd';
import moment from 'moment-timezone';

const { Text, Title } = Typography;
const { Panel } = Collapse;

const OrderSummary = ({ orderHistory, venueTimeZone }) => {
  // Early return if no data
  if (!orderHistory || orderHistory.length === 0) {
    return null;
  }

  // Get the first record to extract order information
  // This assumes all records have the same order information
  const { order } = orderHistory[0];

  // Format the date with the venue's timezone
  const formatDate = date => {
    if (!date) return 'N/A';
    return moment(date)
      .tz(venueTimeZone)
      .format('MM/DD/YYYY hh:mm:ss A');
  };

  return (
    <Card style={{ marginBottom: 24 }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Descriptions title="Order Information" bordered column={1} size="small">
            <Descriptions.Item label="Order ID">{order.id}</Descriptions.Item>
            <Descriptions.Item label="Created At">{formatDate(order.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Renter Name">{order.user ? `${order.user.firstName} ${order.user.lastName}` : 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Renter ID">{order.user ? order.user.id : 'N/A'}</Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} lg={12}>
          <Descriptions title="Event Information" bordered column={1} size="small">
            <Descriptions.Item label="Event Name">{order.event.name}</Descriptions.Item>
            <Descriptions.Item label="Event ID">{order.event.id}</Descriptions.Item>
            <Descriptions.Item label="Venue">
              {order.event.venue.name} (ID: {order.event.venue.id})
            </Descriptions.Item>
            <Descriptions.Item label="Event Dates">
              {formatDate(order.event.startDate)} to {formatDate(order.event.endDate)}
            </Descriptions.Item>
            <Descriptions.Item label="Booking Window">
              {formatDate(order.event.openDate)} to {formatDate(order.event.closeDate)}
            </Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>
    </Card>
  );
};

const AdminOrderHistoryTable = ({ orderHistory, loading, error }) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '16px' }}>
        <Text type="secondary">Loading...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '16px' }}>
        <Text type="danger">Error loading order history: {error.message}</Text>
      </div>
    );
  }

  if (!orderHistory || orderHistory.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '16px' }}>
        <Text type="secondary">No order history found</Text>
      </div>
    );
  }

  const { order } = orderHistory[0];
  const venueTimeZone = order.event.venue.timeZone;

  // State for expandable rows
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // Format the date with the venue's timezone
  const formatDate = date => {
    if (!date) return 'N/A';
    return moment(date)
      .tz(venueTimeZone)
      .format('MM/DD/YYYY hh:mm:ss A');
  };

  // Get admin full name
  const getAdminName = admin => {
    if (!admin) return 'Unknown';
    return `${admin.firstName} ${admin.lastName}`;
  };

  // Column definitions
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      fixed: 'left'
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      fixed: 'left',
      render: date => formatDate(date),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    },
    {
      title: 'Admin',
      key: 'admin',
      width: 150,
      render: (_, record) => getAdminName(record.admin)
    },
    {
      title: 'Admin Email',
      key: 'adminEmail',
      width: 220,
      render: (_, record) => record.admin?.email || 'N/A'
    },
    {
      title: 'Changes',
      key: 'changes',
      render: (_, record) => {
        if (!record.changes || record.changes.length === 0) {
          return <Text type="secondary">No changes</Text>;
        }

        // If there are just a few changes, show them directly
        if (record.changes.length <= 3) {
          return <List size="small" dataSource={record.changes} renderItem={item => <List.Item style={{ padding: '4px 0' }}>{item}</List.Item>} />;
        }

        // If there are many changes, show a summary and make them expandable
        return (
          <Popover
            content={
              <List
                size="small"
                style={{ maxHeight: '400px', overflow: 'auto', width: '500px' }}
                dataSource={record.changes}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
            }
            title="All Changes"
            trigger="click"
            placement="right">
            <Button type="link">{record.changes.length} changes - Click to view</Button>
          </Popover>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Button
          type="link"
          onClick={e => {
            e.stopPropagation();
            toggleExpand(record.id);
          }}>
          {expandedRowKeys.includes(record.id) ? 'Hide Details' : 'Show Details'}
        </Button>
      )
    }
  ];

  // Toggle row expansion
  const toggleExpand = recordId => {
    setExpandedRowKeys(prevKeys => (prevKeys.includes(recordId) ? prevKeys.filter(key => key !== recordId) : [...prevKeys, recordId]));
  };

  // Define expandable row content
  const expandedRowRender = record => {
    const { oldValues, input } = record;

    // Group changes by category for better organization
    const categorizeChanges = changes => {
      const categories = {
        'Order Details': [],
        'Payment Information': [],
        'Add-ons': [],
        Stalls: [],
        RV: [],
        Assignments: [],
        'Status Changes': [],
        Other: []
      };

      changes.forEach(change => {
        if (change.includes('adminNotes') || change.includes('notes') || change.includes('total')) {
          categories['Order Details'].push(change);
        } else if (change.includes('payment') || change.includes('refund') || change.includes('amount') || change.includes('fee')) {
          categories['Payment Information'].push(change);
        } else if (change.includes('Add-on') || change.includes('addon')) {
          categories['Add-ons'].push(change);
        } else if (change.includes('Stall') || change.includes('stall')) {
          categories['Stalls'].push(change);
        } else if (change.includes('RV') || change.includes('rv')) {
          categories['RV'].push(change);
        } else if (change.includes('assign') || change.includes('space')) {
          categories['Assignments'].push(change);
        } else if (change.includes('status') || change.includes('cancel')) {
          categories['Status Changes'].push(change);
        } else {
          categories['Other'].push(change);
        }
      });

      // Remove empty categories
      // eslint-disable-next-line no-unused-vars
      return Object.entries(categories).filter(([_, items]) => items.length > 0);
    };

    const categorizedChanges = categorizeChanges(record.changes || []);

    return (
      <div style={{ padding: '16px 32px', backgroundColor: '#f5f5f5' }}>
        <Collapse defaultActiveKey={['1']} bordered={false}>
          <Panel header="Changes by Category" key="1">
            {categorizedChanges.map(([category, items]) => (
              <div key={category} style={{ marginBottom: '16px' }}>
                <Typography.Title level={5}>{category}</Typography.Title>
                <List
                  size="small"
                  dataSource={items}
                  renderItem={item => (
                    <List.Item style={{ padding: '4px 0' }}>
                      <Text>{item}</Text>
                    </List.Item>
                  )}
                />
              </div>
            ))}
          </Panel>

          <Panel header="Before & After Comparison" key="2">
            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1, marginRight: '16px' }}>
                <Typography.Title level={5}>Before</Typography.Title>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{JSON.stringify(oldValues, null, 2)}</pre>
              </div>
              <div style={{ flex: 1 }}>
                <Typography.Title level={5}>After</Typography.Title>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{JSON.stringify(input, null, 2)}</pre>
              </div>
            </div>
          </Panel>
        </Collapse>
      </div>
    );
  };

  return (
    <>
      <OrderSummary orderHistory={orderHistory} venueTimeZone={venueTimeZone} />

      <Title level={4}>Order History</Title>

      <Table
        dataSource={orderHistory}
        columns={columns}
        rowKey="id"
        expandable={{
          expandedRowRender,
          expandedRowKeys,
          onExpand: (expanded, record) => {
            if (expanded) {
              setExpandedRowKeys([...expandedRowKeys, record.id]);
            } else {
              setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.id));
            }
          }
        }}
        pagination={{
          position: ['bottomCenter'],
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50']
        }}
        scroll={{ x: 'max-content' }}
        bordered
      />
    </>
  );
};

export default AdminOrderHistoryTable;
